import { ChartAreaInteractive } from "@/components/shared/chart-area-interactive";
import { SectionCards } from "@/components/shared/section-cards";
import MainLayout from "@/components/layouts";
import UpcomingAppointmentsTable from "@/app/pmc/home/components/upcoming-appointments-table";
import QuickActions from "@/app/pmc/home/components/quick-actions";
import { CalendarDays, CheckCircle, Redo, RefreshCw } from "lucide-react";
import { useHome } from "./home.hooks";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function PageHeader({
  chartTimeRange,
  onTimeRangeChange,
  onRefresh,
  onRetry,
  showControls = true,
  isError = false,
}: {
  chartTimeRange: string;
  onTimeRangeChange: (value: string) => void;
  onRefresh: () => void;
  onRetry?: () => void;
  showControls?: boolean;
  isError?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">PMC overview and key metrics.</p>
      </div>
      {showControls && (
        <div className="flex items-center gap-2">
          <Select value={chartTimeRange} onValueChange={onTimeRangeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={isError && onRetry ? onRetry : onRefresh}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {isError && onRetry ? "Retry" : "Refresh"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const {
    stats,
    chartData,
    chartTimeRange,
    setChartTimeRange,
    upcomingAppointments,
    loading,
    error,
    refreshData,
    clearError,
  } = useHome();

  const breadcrumb = [
    {
      name: "Dashboard",
      href: "#",
    },
  ];

  const statsCards = stats
    ? [
        {
          name: "Upcoming Bookings",
          value: stats.upcomingBookings.display,
          icon: <CalendarDays size={32} strokeWidth={1} />,
        },
        {
          name: "Completed Appointments",
          value: stats.completedAppointments.display,
          icon: <CheckCircle size={32} strokeWidth={1} />,
        },
        {
          name: "Go Back Rate",
          value: stats.goBackRate.display,
          icon: <Redo size={32} strokeWidth={1} />,
        },
      ]
    : [];

  const handleRetry = () => {
    clearError();
    refreshData();
  };

  if (loading && !stats) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <div className="space-y-6">
          <PageHeader
            chartTimeRange={chartTimeRange}
            onTimeRangeChange={(v) => setChartTimeRange(v as "7d" | "30d" | "90d")}
            onRefresh={refreshData}
            showControls={false}
          />
          <Card className="p-0 bg-primary/2">
            <CardContent className="py-4">
              <QuickActions />
            </CardContent>
          </Card>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="h-8 w-16 animate-pulse bg-gray-200 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex justify-center py-8">
            <p className="text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <div className="space-y-6">
          <PageHeader
            chartTimeRange={chartTimeRange}
            onTimeRangeChange={(v) => setChartTimeRange(v as "7d" | "30d" | "90d")}
            onRefresh={refreshData}
            onRetry={handleRetry}
            isError
          />
          <Card className="p-0 bg-primary/2">
            <CardContent className="py-4">
              <QuickActions />
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-red-600 mb-4">Failed to load dashboard data: {error}</p>
                <Button onClick={handleRetry}>Try Again</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="space-y-6">
        <PageHeader
          chartTimeRange={chartTimeRange}
          onTimeRangeChange={(v) => setChartTimeRange(v as "7d" | "30d" | "90d")}
          onRefresh={refreshData}
        />
        <Card className="p-0 bg-primary/2">
          <CardContent className="py-4">
            <QuickActions />
          </CardContent>
        </Card>
        <SectionCards data={statsCards} />
        <ChartAreaInteractive
          data={chartData}
          timeRange={chartTimeRange}
          onTimeRangeChange={setChartTimeRange}
        />
        <UpcomingAppointmentsTable appointments={upcomingAppointments} />
      </div>
    </MainLayout>
  );
}
